import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as inboundService from '@/services/inboundService';
import type {
  InboundFilter,
  CreateInboundRequest,
  UpdateIQCRequest,
  CompleteIQCRequest,
  InboundStatus,
} from '@/types/inbound';

// ============================================================================
// Query Keys
// ============================================================================

export const INBOUND_QUERY_KEYS = {
  all: ['inboundShipments'] as const,
  lists: () => [...INBOUND_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: InboundFilter) => [...INBOUND_QUERY_KEYS.lists(), filter] as const,
  details: () => [...INBOUND_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...INBOUND_QUERY_KEYS.details(), id] as const,
};

// ============================================================================
// Inbound Shipment Hooks
// ============================================================================

/**
 * Hook to fetch list of inbound shipments with optional filtering
 */
export function useInboundShipments(filter?: InboundFilter) {
  return useQuery(
    INBOUND_QUERY_KEYS.list(filter),
    () => inboundService.listInboundShipments(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

/**
 * Hook to fetch a single inbound shipment by ID
 */
export function useInboundShipment(id: string | undefined) {
  return useQuery(
    INBOUND_QUERY_KEYS.detail(id!),
    () => inboundService.getInboundShipment(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a new inbound shipment
 */
export function useCreateInboundShipment() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateInboundRequest) => inboundService.createInboundShipment(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
        // Also invalidate PO queries since received quantities change
        queryClient.invalidateQueries(['purchaseOrders']);
      },
    }
  );
}

/**
 * Hook to delete an inbound shipment
 */
export function useDeleteInboundShipment() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => inboundService.deleteInboundShipment(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
        queryClient.invalidateQueries(['purchaseOrders']);
      },
    }
  );
}

// ============================================================================
// IQC Hooks
// ============================================================================

/**
 * Hook to update IQC status for a single item
 */
export function useUpdateItemIQC() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ inboundId, request }: { inboundId: string; request: UpdateIQCRequest }) =>
      inboundService.updateItemIQC(inboundId, request),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.detail(variables.inboundId));
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
      },
    }
  );
}

/**
 * Hook to complete IQC for all items (batch update)
 */
export function useCompleteIQC() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ inboundId, request }: { inboundId: string; request: CompleteIQCRequest }) =>
      inboundService.completeIQC(inboundId, request),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.detail(variables.inboundId));
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
      },
    }
  );
}

// ============================================================================
// Status Hooks
// ============================================================================

/**
 * Hook to update inbound shipment status
 */
export function useUpdateInboundStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: InboundStatus }) =>
      inboundService.updateInboundStatus(id, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.detail(variables.id));
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
      },
    }
  );
}

/**
 * Hook to complete an inbound shipment and update inventory
 */
export function useCompleteInbound() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => inboundService.completeInbound(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(INBOUND_QUERY_KEYS.lists());
        // Invalidate inventory queries since stock levels change
        queryClient.invalidateQueries(['inventory']);
        queryClient.invalidateQueries(['purchaseOrders']);
      },
    }
  );
}
